// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { colors, media } from '@toggl/style'

import { IconArrowBigLeft } from '../icons/IconArrow'

type Props = {
  children: React.Node[],
  show: number,
  infiniteLoop?: boolean,
}

export const CarouselNormal = (props: Props) => {
  const { children, show, infiniteLoop } = props

  const [currentIndex, setCurrentIndex] = React.useState(
    infiniteLoop ? show : 0
  )
  const [length, setLength] = React.useState(children.length)

  const [isRepeating, setIsRepeating] = React.useState(
    infiniteLoop && children.length > show
  )
  const [transitionEnabled, setTransitionEnabled] = React.useState(true)

  const [touchPosition, setTouchPosition] = React.useState(null)

  // Set the length to match current children from props
  React.useEffect(() => {
    setLength(children.length)
    setIsRepeating(infiniteLoop && children.length > show)
  }, [children, infiniteLoop, show])

  React.useEffect(() => {
    if (isRepeating) {
      if (currentIndex === show || currentIndex === length) {
        setTransitionEnabled(true)
      }
    }
  }, [currentIndex, isRepeating, show, length])

  const next = () => {
    if (isRepeating || currentIndex < length - show) {
      setCurrentIndex((prevState) => prevState + 1)
    }
  }

  const prev = () => {
    if (isRepeating || currentIndex > 0) {
      setCurrentIndex((prevState) => prevState - 1)
    }
  }

  const handleTouchStart = (e) => {
    const touchDown = e.touches[0].clientX
    setTouchPosition(touchDown)
  }

  const handleTouchMove = (e) => {
    const touchDown = touchPosition

    if (touchDown === null) {
      return
    }

    const currentTouch = e.touches[0].clientX
    const diff = touchDown - currentTouch

    if (diff > 5) {
      next()
    }

    if (diff < -5) {
      prev()
    }

    setTouchPosition(null)
  }

  const handleTransitionEnd = () => {
    if (isRepeating) {
      if (currentIndex === 0) {
        setTransitionEnabled(false)
        setCurrentIndex(length)
      } else if (currentIndex === length + show) {
        setTransitionEnabled(false)
        setCurrentIndex(show)
      }
    }
  }

  const renderExtraPrev = () => {
    let output = []
    for (let index = 0; index < show; index++) {
      output.push(children[length - 1 - index])
    }
    output.reverse()
    return output
  }

  const renderExtraNext = () => {
    let output = []
    for (let index = 0; index < show; index++) {
      output.push(children[index])
    }
    return output
  }

  return (
    <Root className={`show-${show}`}>
      <CarouselWrapper>
        {/* You can alwas change the content of the button to other things */}
        <ButtonWrapper>
          <div>
            {(isRepeating || currentIndex > 0) && (
              <ButtonArrow onClick={prev}>
                <IconArrowBigLeft />
              </ButtonArrow>
            )}
          </div>
          <div>
            {(isRepeating || currentIndex < length - show) && (
              <RightArrow onClick={next}>
                <IconArrowBigLeft />
              </RightArrow>
            )}
          </div>
        </ButtonWrapper>

        <CarouselContentWrapper
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        >
          <CarouselContent
            style={{
              transform: `translateX(-${currentIndex * (100 / show)}%)`,
              transition: !transitionEnabled ? 'none' : undefined,
            }}
            onTransitionEnd={() => handleTransitionEnd()}
          >
            {length > show && isRepeating && renderExtraPrev()}
            {children}
            {length > show && isRepeating && renderExtraNext()}
          </CarouselContent>
        </CarouselContentWrapper>
        {/* You can alwas change the content of the button to other things */}
      </CarouselWrapper>
    </Root>
  )
}

const CarouselContent = styled.div`
  display: flex;
  transition: all 250ms linear;
  -ms-overflow-style: none;
  scrollbar-width: none;
  > * {
    flex: 0 0 75%;
  }
  &::-webkit-scrollbar {
    display: none;
  }
`

const Root: React.ComponentType<any> = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: calc(100% + 60px);
  margin-right: -35px;
  margin-left: -30px;
  ${media.mq[1]} {
    width: calc(100% + 40px);
    margin-left: -20px;
    margin-right: -20px;
    &.show-3 ${CarouselContent} > * {
      flex: 0 0 calc(100% / 3);
    }
  }
  &.show-5 {
    ${CarouselContent} > * {
      flex: 0 0 45%;
      ${media.mq[1]} {
        flex: 0 0 calc(100% / 5);
      }
      ${media.mq[2]} {
        flex: 0 0 calc(100% / 5);
      }
    }
    ${media.mq[1]} {
      width: calc(100% + 30px);
      margin-left: -15px;
      margin-right: -15px;
    }
  }
`

const CarouselWrapper = styled.div`
  display: flex;
  width: 100%;
  position: relative;
`

const CarouselContentWrapper = styled.div`
  overflow-y: auto;
  width: 100%;
  height: 100%;
  padding-left: 30px;
  ${media.mq[1]} {
    overflow: hidden;
    padding-left: 0;
  }
  ${media.mq[2]} {
    padding-left: 0;
  }
`

const ButtonWrapper = styled.div`
  display: none;
  ${media.mq[1]} {
    display: block;
    position: absolute;
    right: 15px;
    transform: translateY(-90%);
    > div {
      width: 48px;
      margin: 5px;
      display: inline-block;
    }
  }
`

const ButtonArrow = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: ${colors.yellow100};
  border-radius: 100%;
  border: none;
  cursor: pointer;
  svg {
    width: 30px;
  }
  &:hover {
    background: ${colors.darkPurple40};
  }
`
const RightArrow = styled(ButtonArrow)`
  transform: scaleX(-1);
`
